import SecureRandom from "@/util/SecureRandom";
import ByteEncoder from "@/util/byteEncoder";
import Base64Encoder from "@/util/base64encoder";
import Base = Mocha.reporters.Base;
import Account from "@/views/Account.vue";
import FileAES from "@/util/fileAES";
import User from "@/models/User";

export default class AccountDataAES {
    private key: CryptoKey;

    public static readonly ivLength: number = 16; // Bytes not Bits (12 for AES-GCM, 16 for AES-CBC/CTR)
    public static readonly keyLength: number = 32; // Bytes not Bits!
    public static readonly opMode: string = 'AES-CBC';

    private static masterKeyDerivationRounds: number = 100001;
    private static masterKeyDervivationHash: string = 'SHA-512';
    private static masterHashDerivationRounds: number = 100001;
    private static masterHashDervivationHash: string = 'SHA-512';

    constructor(key: CryptoKey) {
        this.key = key;
    }

    public static async fromAccountData(masterKey: CryptoKey, account: User) {
        const accountKeyEncrypted: string = account.accountKey;
        const accountKeyIv: Uint8Array = Base64Encoder.stringToBytes(account.accountKeyIv);
        const temp: AccountDataAES = new AccountDataAES(masterKey);
        const accountKey: CryptoKey | null = await temp.unwrapKey(accountKeyEncrypted, accountKeyIv);
        if (!accountKey) {
            throw new Error("Could not deserialize account key");
        }
        return new AccountDataAES(accountKey);
    }

    public getKey(): CryptoKey {
        return this.key;
    }

    public static generateKey(): Promise<CryptoKey> {
        return crypto.subtle.generateKey(
            {
                name: AccountDataAES.opMode,
                length: AccountDataAES.keyLength * 8,
            },
            true,
            ['encrypt', 'decrypt'],
        );
    }

    public static generateIV(): Uint8Array {
        return SecureRandom.generate(AccountDataAES.ivLength);
    }

    public encryptChunk(plainBytes: ArrayBuffer, iv: Uint8Array): Promise<ArrayBuffer> {
        return crypto.subtle.encrypt({ name: AccountDataAES.opMode, iv, tagLength: 128 }, this.key, plainBytes);
    }

    public decryptChunk(encryptedBytes: ArrayBuffer, iv: Uint8Array): Promise<ArrayBuffer> {
        return crypto.subtle.decrypt({ name: AccountDataAES.opMode, iv, tagLength: 128 }, this.key, encryptedBytes);
    }

    public async encryptStringAndConvertToBase64(payload: string, iv: Uint8Array): Promise<string> {
        const plainBytes: Uint8Array = ByteEncoder.stringToBytes(payload);
        const encryptedBytes = await this.encryptChunk(plainBytes, iv);
        return Base64Encoder.bytesToString(new Uint8Array(encryptedBytes));
    }

    public async decryptStringFromBase64Encoded(payload: string, iv: Uint8Array): Promise<string> {
        const encryptedBytes = Base64Encoder.stringToBytes(payload);
        const plainBytes = await this.decryptChunk(encryptedBytes, iv);
        return ByteEncoder.bytesToString(new Uint8Array(plainBytes));
    }

    public async wrapKey(keyToWrap: CryptoKey, iv: Uint8Array): Promise<string> {
        const keyAsB64: string = await AccountDataAES.exportKeyToBase64(keyToWrap);
        return await this.encryptStringAndConvertToBase64(keyAsB64, iv);
    }

    public async unwrapKey(keyAsWrappedB64: string, iv: Uint8Array): Promise<CryptoKey | null> {
        const decrypted: string = await this.decryptStringFromBase64Encoded(keyAsWrappedB64, iv);
        return await AccountDataAES.loadKeyFromBase64(decrypted);
    }

    public static async generateMasterKeyFromUserPassword(username: string, password: string): Promise<CryptoKey | null> {
        try {
            const keyMaterial = await crypto.subtle.importKey(
                'raw',
                ByteEncoder.stringToBytes(password),
                'PBKDF2',
                false,
                ['deriveBits', 'deriveKey'],
            );

            const salt: Uint8Array = ByteEncoder.stringToBytes(username);

            const keyBits = await crypto.subtle.deriveBits({
                    name: 'PBKDF2',
                    salt,
                    iterations: AccountDataAES.masterKeyDerivationRounds,
                    hash: AccountDataAES.masterKeyDervivationHash,
                },
                keyMaterial,
                AccountDataAES.keyLength * 8,
            );

            const masterKey: CryptoKey = await crypto.subtle.importKey(
                'raw',
                keyBits,
                {name: AccountDataAES.opMode, length: AccountDataAES.keyLength},
                true,
                ['encrypt', 'decrypt'],
            );

            return masterKey;
        } catch (e) {
            console.error("Failed to derive master key from user/pass", e);
            return null;
        }
    }

    public static async generateAuthenticationHashFromMasterKey(masterKey: CryptoKey, password: string): Promise<string> {
        try {
            const salt: Uint8Array = ByteEncoder.stringToBytes(password);
            //Convert the main key from AES decrypt/encrypt to just PBKDF:
            const rawMasterKey = await crypto.subtle.exportKey("raw", masterKey);
            const pbkdfReadyMasterKey =  await crypto.subtle.importKey(
                'raw',
                rawMasterKey,
                'PBKDF2',
                false,
                ['deriveBits', 'deriveKey'],
            );

            const bytes: ArrayBuffer = await crypto.subtle.deriveBits({
                    name: 'PBKDF2',
                    salt,
                    iterations: AccountDataAES.masterHashDerivationRounds,
                    hash: AccountDataAES.masterHashDervivationHash,
                },
                pbkdfReadyMasterKey,
                AccountDataAES.keyLength * 8,
            );

            return Base64Encoder.bytesToString(new Uint8Array(bytes));
        } catch (e) {
            console.error("Failed to compute master hash from master key", e);
            return "";
        }
    }

    public static async exportKeyToBase64(key: CryptoKey): Promise<string> {
        const bytes = await crypto.subtle.exportKey("raw", key) as ArrayBuffer;
        const b64String = Base64Encoder.bytesToString(new Uint8Array(bytes));
        return Promise.resolve(b64String);
    }

    public static async loadKeyFromBase64(base64Input: string): Promise<CryptoKey> {
        const bytes = Base64Encoder.stringToBytes(base64Input);
        return crypto.subtle.importKey(
            "raw",
            bytes,
            {name: AccountDataAES.opMode, length: AccountDataAES.keyLength},
            true,
            ['encrypt', 'decrypt'],
        );
    }

}
